import { z } from 'zod'
import { ModelOrganization } from './organization'
import { Image, ModelStatus, regexEmail, regexPhone, regexWeb } from './utils'

export type ModelCompany = Pick<
  Company,
  '_id' | 'name' | 'slug' | 'tid' | 'childCompanyIds'
> & {
  meta?: {
    logo?: Image
  }
}

type CompanyMeta = {
  address?: string
  address2?: string
  city?: string
  stateAbbr?: string
  postalCode?: string
  phone?: string
  fax?: string
  email?: string
  website?: string
  linkedIn?: string
  logo?: Image
}

export type Company = {
  _id: string
  tid: string
  bacId?: string
  rapId?: number
  agentBookId?: string
  created: string
  modified: string
  organization: ModelOrganization
  slug: string
  name: string
  status: ModelStatus
  meta: CompanyMeta
  childCompanyIds?: string[] | null
  disabledIntegrations?: string[]
  deleted?: {
    date: string
    reason: string
    previousStatus?: string
    meta?: {
      mergedIntoCompanyId?: string
    }
  }
}

type ModelCompanyInput = Pick<
  Company,
  '_id' | 'tid' | 'slug' | 'name' | 'childCompanyIds'
> & {
  meta?: {
    logo?: Image
  }
}

export function getModelCompany(company: ModelCompanyInput): ModelCompany {
  const modelCompany: ModelCompany = {
    _id: company._id,
    tid: company.tid,
    slug: company.slug,
    name: company.name,
  }

  if (company.childCompanyIds) {
    modelCompany.childCompanyIds = company.childCompanyIds
  }

  if (company.meta?.logo) {
    modelCompany.meta = {
      logo: company.meta.logo,
    }
  }

  return modelCompany
}

type ChildCompanyIdsInput = Company['childCompanyIds'] | string

export const ChildCompanyIdsSchema: z.ZodType<
  Company['childCompanyIds'],
  z.ZodTypeDef,
  ChildCompanyIdsInput
> = z
  .union([z.array(z.string()), z.string()])
  .transform((value) => (typeof value === 'string' ? [value] : value))
  .optional()
  .nullable()

export const CompanySchema = z.object({
  _id: z.string(),
  tid: z.string(),
  created: z.string(),
  modified: z.string(),
  organization: z.custom<ModelOrganization>(),
  slug: z.string(),
  name: z.string(),
  status: z.nativeEnum(ModelStatus),
  agentBookId: z.string().optional(),
  meta: z.object({
    address: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    stateAbbr: z.string().optional(),
    postalCode: z.string().optional(),
    phone: z
      .string()
      .optional()
      .refine(regexPhone, 'Please enter a valid phone number'),
    fax: z
      .string()
      .optional()
      .refine(regexPhone, 'Please enter a valid phone number'),
    email: z
      .string()
      .optional()
      .refine(regexEmail, 'Please enter a valid email address'),
    website: z
      .string()
      .optional()
      .refine(regexWeb, 'Please enter a valid wed address'),
    linkedIn: z
      .string()
      .optional()
      .refine(regexWeb, 'Please enter a valid wed address'),
    logo: z
      .object({
        _id: z.string(),
        url: z.string(),
        thumbUrl: z.string(),
      })
      .optional(),
  }),
  childCompanyIds: ChildCompanyIdsSchema,
  disabledIntegrations: z.array(z.string()).optional(),
  deleted: z
    .object({
      date: z.string(),
      reason: z.string(),
      previousStatus: z.string().optional(),
      meta: z
        .object({
          mergedIntoCompanyId: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
}) satisfies z.ZodType<Company, z.ZodTypeDef, unknown>
